import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, ytId } from "@/lib/api";

export default function VideosTab() {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");

  const load = () => api.get("/videos").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  if (!items) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const add = async () => {
    if (!ytId(url)) return toast.error("Paste a valid YouTube link.");
    await api.post("/admin/videos", { url, title });
    setUrl("");
    setTitle("");
    toast.success("Video added to the playlist.");
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post("/admin/reorder", { collection: "videos", ids: next.map((v) => v.id) });
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const del = async (v) => {
    if (!window.confirm("Remove this video?")) return;
    await api.delete(`/admin/videos/${v.id}`);
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8 rounded-3xl border border-line bg-white p-5 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-mute">Add a YouTube review</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="admin-video-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            className="flex-1 border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs"
          />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs sm:w-52"
          />
          <button data-testid="admin-video-add-button" onClick={add} className="flex items-center justify-center gap-2 bg-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-srs">
            <Plus size={14} /> Add
          </button>
        </div>
        <p className="mt-3 text-xs text-subtle">Videos autoplay muted on the site and advance in this order. First in the list plays first.</p>
      </div>

      <div className="divide-y divide-line border border-line bg-white" data-testid="admin-videos-list">
        {items.map((v, i) => (
          <div key={v.id} className="flex items-center gap-4 p-4" data-testid={`admin-video-row-${i}`}>
            <span className="w-7 font-mono text-[11px] text-subtle">{String(i + 1).padStart(2, "0")}</span>
            <img src={`https://i.ytimg.com/vi/${ytId(v.url)}/default.jpg`} alt="" className="h-12 w-16 border border-line object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{v.title || `Review ${i + 1}`}</p>
              <p className="truncate font-mono text-[10px] text-subtle">{v.url}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move up"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move down"><ArrowDown size={15} /></button>
              <button onClick={() => del(v)} className="p-2 text-mute hover:text-srs" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="p-8 text-center text-sm text-subtle">No videos yet — the public site shows a curated empty state until you add one.</p>}
      </div>
    </div>
  );
}
